import React from 'react'

const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <div className="min-h-screen w-full flex bg-slate-50 relative overflow-hidden">
            <div className="flex w-full z-10">
                <div className="hidden lg:flex w-1/2 flex-col justify-between p-12 bg-white/40 backdrop-blur-3xl border-r border-white/50 relative overflow-hidden shadow-xl">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-size-[24px_24px]"></div>

                    <div className="relative z-10 flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-white shadow-lg shadow-primary/30">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-heart-pulse text-primary" aria-hidden="true"><path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"></path><path d="M3.22 13H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"></path></svg>
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                            Sadguru HRM
                        </h1>
                    </div>

                    <div className="relative z-10 max-w-lg mt-auto mb-auto">

                        <h2 className="text-4xl mt-5 font-extrabold tracking-tight text-slate-900 mb-6 leading-tight">
                            HRMS built for a <br />
                            <span className="text-primary">
                                non-profit hospital.
                            </span>
                        </h2>
                        <p className="text-base text-slate-600 mb-10 leading-relaxed font-medium">
                            Manage doctors, nurses, payroll and compliance for a 200-bed trust hospital — with role-based workflows, real-time approvals and India-ready statutory deductions.
                        </p>

                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/05 border border-white/10">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-users" aria-hidden="true">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2">
                                        </path>
                                        <path d="M16 3.128a4 4 0 0 1 0 7.744">
                                        </path>
                                        <path d="M22 21v-2a4 4 0 0 0-3-3.87">
                                        </path>
                                        <circle cx="9" cy="7" r="4">
                                        </circle>
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium">200-bed hospital roster</div>
                                    <div className="text-xs text-slate-500 mt-0.5">18 employees across 8 departments</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/05 border border-white/10">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calendar" aria-hidden="true">
                                        <path d="M8 2v4">
                                        </path>
                                        <path d="M16 2v4">
                                        </path>
                                        <rect width="18" height="18" x="3" y="4" rx="2">
                                        </rect>
                                        <path d="M3 10h18">
                                        </path>
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium">Leave &amp; WFH workflows</div>
                                    <div className="text-xs text-slate-500 mt-0.5">Manager → HR approval chains</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/05 border border-white/10">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-wallet" aria-hidden="true">
                                        <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1">
                                        </path>
                                        <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4">
                                        </path>
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium">3-stage payroll</div>
                                    <div className="text-xs text-slate-500 mt-0.5">Draft → Verified → Released</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/05 border border-white/10">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield" aria-hidden="true">
                                        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z">
                                        </path>
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium">Credential tracking</div>
                                    <div className="text-xs text-slate-500 mt-0.5">NMC, MCI registrations &amp; alerts</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/05 border border-white/10">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chart-column" aria-hidden="true">
                                        <path d="M3 3v16a2 2 0 0 0 2 2h16">
                                        </path>
                                        <path d="M18 17V9">
                                        </path>
                                        <path d="M13 17V5">
                                        </path>
                                        <path d="M8 17v-3">
                                        </path>
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium">Live analytics</div>
                                    <div className="text-xs text-slate-500 mt-0.5">Department, attendance, payroll</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 text-sm text-slate-500 font-semibold">
                        © {new Date().getFullYear()} Sadguru HRM. All rights reserved.
                    </div>
                </div>

                {/* Right side: Login Form */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative">
                    <div className="w-full max-w-md">
                        <div className="bg-white/80 backdrop-blur-2xl rounded-[2.5rem] shadow-[0_8px_40px_-12px_rgba(0,0,0,0.1)] border border-white p-8 sm:p-10 transition-all duration-500 hover:shadow-[0_16px_60px_-15px_rgba(0,0,0,0.15)] relative overflow-hidden group">
                            {children}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AuthLayout